<template>
    <modal
        id="item-modal"
        :adaptive="true"
        :class="{ 'mobile-item-modal': isMobile }"
        :maxWidth="modalWidth"
        :pivotY="isMobile ? 1 : 0.5"
        :reset="true"
        :scrollable="true"
        :styles="{ marginTop }"
        :width="modalWidth"
        class="modal-visible-overflow overscroll-none"
        height="auto"
        name="item-modal"
        transition="pop-out"
        @closed="closed"
        @opened="opened"
        @before-open="beforeOpen"
    >
        <div
            v-if="item"
            id="modal-id"
            v-touch:end="endHandler"
            v-touch:start="startHandler"
            v-touch:swipe.bottom="swipeHandler"
            :class="modalLayoutClass"
            class="item-modal-content relative flex flex-col w-full"
        >
            <div
                :class="{ '!absolute -right-2 -top-2': isTwoColumnLayout }"
                class="sticky z-50 top-0 flex justify-end h-0"
            >
                <div class="mr-4 mt-4">
                    <button
                        :class="{
                            'text-muted': !item.image && !item.video,
                            hidden: !itemHasModifiers && !item.image
                        }"
                        aria-label="Close item"
                        class="no-highlight p-1.5 text-primary bg-secondary rounded-full shadow-md"
                        data-testid="item-modal-close"
                        tabindex="0"
                        @click="hideItemModal()"
                    >
                        <long-back-icon
                            v-if="isBao"
                            class="w-6 h-6 rotate-180"
                        />
                        <svg
                            v-else
                            class="w-6 h-6"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                clip-rule="evenodd"
                                d="M7.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l2.293 2.293a1 1 0 010 1.414z"
                                fill-rule="evenodd"
                            ></path>
                        </svg>
                    </button>
                </div>
            </div>
            <div
                v-if="item.image || item.video"
                id="imageContainer"
                :class="{
                    'item-modal-image-shadow relative': item.image,
                    'item-modal-image-shadow-fade':
                        item.image &&
                        isLargeImageStyle &&
                        !item.video &&
                        !isTwoColumnLayout,
                    'bg-black': item.video,
                    'item-modal-image-bg': !item.video && item.image,
                    'skeleton-loading-background': item.image && !item.video
                }"
                class="product-item-modal-details overflow-hidden sm:rounded-t-md rounded-t-lg select-none flex-auto"
            >
                <ProductVideo v-if="item.video" :playback-id="item.video" />
                <img
                    v-if="!item.video && item.image"
                    id="itemModalImage"
                    :alt="item.name"
                    :class="{
                        'item-modal-main-image-large': isLargeImageStyle,
                        'item-modal-image-loaded': imageLoaded
                    }"
                    :src="getImageUrl(item.image, isTwoColumnLayout)"
                    :srcset="getImageSrcset(item.image, isTwoColumnLayout)"
                    class="item-modal-main-image overflow-hidden sm:rounded-t-md rounded-t-lg relative w-full bg-center object-cover"
                />
            </div>
            <div
                :class="{
                    'transition-height duration-300 ease-in-out': isOpened,
                    'rounded-t-lg': !item.image && !item.video
                }"
                class="item-modal-body relative rounded-b-none sm:rounded-b-md flex flex-col z-20"
            >
                <div class="overflow-y-auto">
                    <div
                        ref="contentWrapper"
                        class="mb-6 transition-height duration-300 ease-in-out"
                    >
                        <transition
                            :duration="300"
                            mode="out-in"
                            name="fade"
                            @enter="enterTransition"
                            @before-enter="beforeEnterTransition"
                            @after-enter="afterEnterTransition"
                        >
                            <div v-if="tab === 0" :key="1">
                                <div
                                    class="product-item-modal-details select-none flex-auto overscroll-contain sm:overscroll-none"
                                >
                                    <div
                                        :class="{
                                            'border-b border-gray-200':
                                                itemHasModifiers,
                                            'flex flex-col items-center':
                                                isContentCentered
                                        }"
                                        class="relative p-6 py-4 select-none"
                                    >
                                        <h4
                                            :class="{
                                                'pb-1': item.description,
                                                'text-center': isContentCentered
                                            }"
                                            class="break-word w-10/12 text-subheading font-heading text-xl font-bold"
                                        >
                                            {{ item.name }}
                                        </h4>
                                        <p
                                            :class="{
                                                'text-center': isContentCentered
                                            }"
                                            class="text-itembody whitespace-pre-line text-sm"
                                        >
                                            {{ item.description }}
                                        </p>
                                        <div
                                            class="flex w-full"
                                            :class="
                                                isContentCentered && !stockValue
                                                    ? 'justify-center items-center'
                                                    : 'justify-between items-end'
                                            "
                                        >
                                            <div
                                                :class="{
                                                    'flex flex-col items-center':
                                                        isContentCentered &&
                                                        !stockValue
                                                }"
                                            >
                                                <div class="flex">
                                                    <div
                                                        v-if="
                                                            areAllergens &&
                                                            !isAllergenDisplayExtended
                                                        "
                                                        class="no-highlight mt-2 cursor-pointer inline-flex"
                                                        @click="tab = 1"
                                                    >
                                                        <span
                                                            class="text-muted underline text-sm select-none"
                                                        >
                                                            {{
                                                                $t(
                                                                    'itemModal.allergens'
                                                                )
                                                            }}
                                                        </span>
                                                    </div>
                                                    <div
                                                        v-if="
                                                            item.ingredients &&
                                                            item.ingredients
                                                                .length > 0 &&
                                                            areAllergens &&
                                                            !isAllergenDisplayExtended
                                                        "
                                                        class="inline-flex mt-1"
                                                    >
                                                        <span class="mx-2"
                                                            >•</span
                                                        >
                                                    </div>
                                                    <div
                                                        v-if="
                                                            item.ingredients &&
                                                            item.ingredients
                                                                .length > 0
                                                        "
                                                        class="no-highlight mt-2 cursor-pointer inline-flex"
                                                        @click="tab = 2"
                                                    >
                                                        <span
                                                            class="text-muted underline text-sm select-none"
                                                        >
                                                            {{
                                                                $t(
                                                                    'itemModal.ingredients'
                                                                )
                                                            }}
                                                        </span>
                                                    </div>
                                                    <div
                                                        v-if="
                                                            item.nutrition &&
                                                            (item.nutrition
                                                                .carbs !==
                                                                null ||
                                                                item.nutrition
                                                                    .fats !==
                                                                    null ||
                                                                item.nutrition
                                                                    .protein !==
                                                                    null)
                                                        "
                                                        class="inline-flex"
                                                    >
                                                        <span class="mx-2"
                                                            >•</span
                                                        >
                                                    </div>
                                                    <div
                                                        v-if="
                                                            item.nutrition &&
                                                            (item.nutrition
                                                                .carbs !==
                                                                null ||
                                                                item.nutrition
                                                                    .fats !==
                                                                    null ||
                                                                item.nutrition
                                                                    .protein !==
                                                                    null)
                                                        "
                                                        class="no-highlight mt-2 cursor-pointer inline-flex"
                                                        @click="tab = 3"
                                                    >
                                                        <span
                                                            class="text-muted underline text-sm select-none"
                                                        >
                                                            {{
                                                                $t(
                                                                    'itemModal.macroNutrients'
                                                                )
                                                            }}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div
                                                    v-if="
                                                        areAllergens &&
                                                        isAllergenDisplayExtended
                                                    "
                                                    class="flex flex-col mt-2"
                                                    :class="{
                                                        'flex flex-col items-center':
                                                            isContentCentered &&
                                                            !stockValue
                                                    }"
                                                >
                                                    <div
                                                        v-if="
                                                            allergensList &&
                                                            allergensList.length
                                                        "
                                                        class="flex gap-1 items-start"
                                                    >
                                                        <h3
                                                            class="text-subheading font-semibold"
                                                        >
                                                            {{
                                                                $t(
                                                                    'itemModal.containAllergens'
                                                                )
                                                            }}:
                                                        </h3>
                                                        <h4
                                                            class="text-muted block text-sm leading-6 mt-px font-semibold"
                                                        >
                                                            {{
                                                                allergensList.join(
                                                                    ', '
                                                                )
                                                            }}
                                                        </h4>
                                                    </div>

                                                    <div
                                                        v-if="
                                                            possibleAllergensList &&
                                                            possibleAllergensList.length
                                                        "
                                                        class="flex gap-1 items-start"
                                                    >
                                                        <h3
                                                            class="text-subheading block font-semibold text-nowrap"
                                                        >
                                                            {{
                                                                $t(
                                                                    'itemModal.mayContainAllergens'
                                                                )
                                                            }}:
                                                        </h3>
                                                        <h4
                                                            class="text-muted block text-sm leading-6 mt-px font-semibold"
                                                        >
                                                            {{
                                                                possibleAllergensList.join(
                                                                    ', '
                                                                )
                                                            }}
                                                        </h4>
                                                    </div>
                                                </div>

                                                <div
                                                    v-if="
                                                        item.nutrition &&
                                                        item.nutrition
                                                            .calories !==
                                                            null &&
                                                        item.nutrition
                                                            .calories !==
                                                            undefined
                                                    "
                                                    class="mt-2"
                                                >
                                                    <p
                                                        class="text-itembody whitespace-pre-line text-sm"
                                                    >
                                                        {{ totalItemCalories }}
                                                        kcal
                                                    </p>
                                                </div>
                                            </div>

                                            <div>
                                                <span
                                                    v-if="stockValue"
                                                    class="inline-flex text-nowrap items-center rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary ring-1 ring-blue-700/10 ring-inset"
                                                >
                                                    {{ stockValue }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="item.deposit" class="px-6 pb-3">
                                    <p class="text-muted text-sm">
                                        {{ $t('itemModal.depositIncluded') }}:
                                        {{
                                            (item.deposit / 100)
                                                | customCurrency
                                        }}
                                    </p>
                                </div>

                                <item-modifiers
                                    v-if="itemHasModifiers"
                                    v-model="item.modifierGroups"
                                    :item="{ price: item.price }"
                                />
                                <div
                                    v-if="shouldShowItemNotes"
                                    class="item-modal-details flex-auto pb-1 overflow-y-auto"
                                >
                                    <div
                                        :class="{
                                            'cursor-pointer':
                                                !shouldExpandItemNotes
                                        }"
                                        class="select-none flex flex-row items-start justify-between px-6 py-3.5 bg-page"
                                        @click="
                                            !shouldExpandItemNotes
                                                ? toggleAccordion(
                                                      $event,
                                                      $refs['itemNotes']
                                                  )
                                                : false
                                        "
                                    >
                                        <h4
                                            class="text-sm text-subheading font-bold pointer-events-none"
                                        >
                                            {{
                                                $store.state.venue.venue
                                                    .itemNoteLabel ||
                                                $t('itemModal.addNotes')
                                            }}
                                        </h4>
                                        <div
                                            v-if="!shouldExpandItemNotes"
                                            :class="{
                                                'rotate-90': true
                                            }"
                                            class="caret text-secondaryactions transform transition duration-300 ease-in-out pointer-events-none"
                                        >
                                            <dropdown-icon
                                                class="pointer-events-none"
                                            />
                                        </div>
                                    </div>
                                    <div
                                        ref="itemNotes"
                                        :class="{
                                            collapsed: !shouldExpandItemNotes
                                        }"
                                        class="accordion overflow-hidden transition transition-height duration-300 ease-in-out"
                                    >
                                        <div class="relative px-6 mt-4">
                                            <textarea
                                                id="notes"
                                                v-model.trim="item.notes"
                                                :class="{
                                                    'ring ring-red-200 border-red-300':
                                                        $v.item.notes.$error
                                                }"
                                                class="email-input block pt-4 px-4 w-full text-sm bg-secondary border rounded shadow-sm"
                                                name="notes"
                                                required
                                                rows="2"
                                                type="text"
                                            ></textarea>
                                            <label
                                                class="email-input-label absolute pt-1 px-4 rounded pointer-events-none select-none"
                                                for="notes"
                                            >
                                                {{
                                                    $store.state.venue.venue
                                                        .itemNotePlaceholder ||
                                                    $t(
                                                        'itemModal.notesRequests'
                                                    )
                                                }}
                                            </label>
                                        </div>
                                        <div class="flex justify-end px-6">
                                            <span
                                                v-show="$v.item.notes.$error"
                                                class="flex flex-auto justify-start my-2 text-red-500 text-xs"
                                            >
                                                {{
                                                    item?.note?.length > 150
                                                        ? $t(
                                                              'orderNotes.errors.tooLong'
                                                          )
                                                        : 'required'
                                                }}
                                            </span>
                                            <span
                                                :class="{
                                                    'text-red-500':
                                                        $v.item.notes.$error
                                                }"
                                                class="flex justify-end my-2 text-xs"
                                            >
                                                {{
                                                    `${item?.notes?.length}/150`
                                                }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div
                                v-else-if="tab === 1"
                                :key="2"
                                ref="allergensTab"
                                class="z-20"
                            >
                                <div
                                    class="no-highlight mt-2 flex items-center cursor-pointer text-muted px-6"
                                    @click="tab = 0"
                                >
                                    <svg
                                        aria-hidden="true"
                                        class="w-4 h-4 mr-2"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="1.5"
                                        viewBox="0 0 24 24"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        ></path>
                                    </svg>

                                    <span class="underline text-sm select-none">
                                        {{ $t('itemModal.details') }}
                                    </span>
                                </div>
                                <div
                                    :class="{ 'pt-6': !item.image }"
                                    class="product-item-modal-details relative flex-auto overflow-y-auto overscroll-contain"
                                >
                                    <div
                                        v-if="
                                            allergensList &&
                                            allergensList.length > 0
                                        "
                                        class="mt-6 px-6"
                                    >
                                        <h3
                                            class="text-subheading font-semibold"
                                        >
                                            {{
                                                $t('itemModal.containAllergens')
                                            }}
                                        </h3>

                                        <div class="grid grid-cols-2 mt-2">
                                            <template
                                                v-for="tag in allergensList"
                                            >
                                                <h4
                                                    v-if="tag"
                                                    :key="tag"
                                                    class="pb-1 text-muted text-sm font-semibold"
                                                >
                                                    {{ tag }}
                                                </h4>
                                            </template>
                                        </div>
                                    </div>

                                    <div
                                        v-if="
                                            possibleAllergensList &&
                                            possibleAllergensList.length > 0
                                        "
                                        class="mt-6 px-6"
                                    >
                                        <h3
                                            class="text-subheading font-semibold"
                                        >
                                            {{
                                                $t(
                                                    'itemModal.mayContainAllergens'
                                                )
                                            }}
                                        </h3>

                                        <div class="grid grid-cols-2 mt-2">
                                            <template
                                                v-for="tag in possibleAllergensList"
                                            >
                                                <h4
                                                    v-if="tag"
                                                    :key="tag"
                                                    class="pb-1 text-muted text-sm font-semibold"
                                                >
                                                    {{ tag }}
                                                </h4>
                                            </template>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div
                                v-else-if="tab === 2"
                                :key="3"
                                ref="ingredientsTab"
                                class="z-20"
                            >
                                <div
                                    class="no-highlight mt-2 flex items-center cursor-pointer text-muted px-6"
                                    @click="tab = 0"
                                >
                                    <svg
                                        aria-hidden="true"
                                        class="w-4 h-4 mr-2"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="1.5"
                                        viewBox="0 0 24 24"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        ></path>
                                    </svg>

                                    <span class="underline text-sm select-none">
                                        {{ $t('itemModal.details') }}
                                    </span>
                                </div>
                                <div
                                    :class="{ 'pt-6': !item.image }"
                                    class="product-item-modal-details relative flex-auto overflow-y-auto overscroll-contain"
                                >
                                    <div
                                        v-if="
                                            item.ingredients &&
                                            item.ingredients.length > 0
                                        "
                                        class="mt-6 px-6"
                                    >
                                        <h3
                                            class="text-subheading font-semibold"
                                        >
                                            {{ $t('itemModal.ingredients') }}
                                        </h3>

                                        <p class="mt-2 text-muted text-sm">
                                            {{ item.ingredients.join(', ') }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div
                                v-else-if="tab === 3"
                                :key="4"
                                ref="macroNutrientsTab"
                                class="z-20"
                            >
                                <div
                                    class="no-highlight mt-2 flex items-center cursor-pointer text-muted px-6"
                                    @click="tab = 0"
                                >
                                    <svg
                                        aria-hidden="true"
                                        class="w-4 h-4 mr-2"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="1.5"
                                        viewBox="0 0 24 24"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        ></path>
                                    </svg>

                                    <span class="underline text-sm select-none">
                                        {{ $t('itemModal.details') }}
                                    </span>
                                </div>
                                <div
                                    :class="{ 'pt-6': !item.image }"
                                    class="product-item-modal-details relative flex-auto overflow-y-auto overscroll-contain"
                                >
                                    <macro-calculator
                                        :total-carbs="totalItemCarbs"
                                        :total-fats="totalItemFats"
                                        :total-protein="totalItemProtein"
                                    />
                                    <nutrition-breakdown-table
                                        :total-calories="totalItemCalories"
                                        :total-carbs="totalItemCarbs"
                                        :total-carbs-sugars="
                                            totalItemCarbsSugars
                                        "
                                        :total-fat-saturates="
                                            totalItemFatSaturates
                                        "
                                        :total-fats="totalItemFats"
                                        :total-fibre="totalItemFibre"
                                        :total-protein="totalItemProtein"
                                        :total-salt="totalItemSalt"
                                    />
                                </div>
                            </div>
                        </transition>
                    </div>
                    <div
                        v-if="soldOut"
                        class="-mt-6 pb-2 text-center text-red-600"
                    >
                        {{
                            item.inventory > 0
                                ? $t('itemModal.almostSoldOut', {
                                      value: item.inventory
                                  })
                                : $t('itemModal.soldOut')
                        }}
                    </div>
                </div>
                <!--                <menu-recommendations-->
                <!--                    v-if="hasRecommendationsEnabled"-->
                <!--                    :instance-id="'modal'"-->
                <!--                    key="modal"-->
                <!--                    :current-item="item"-->
                <!--                    class="px-4 mb-4"-->
                <!--                />-->
                <div
                    v-if="!visualMenu && !allergenDisabled"
                    ref="addToCart"
                    class="item-modal-footer footer-blurred z-50 sticky bottom-0 mt-auto p-3 pb-5 relative"
                >
                    <div
                        v-if="shouldShowMacroWidget"
                        class="absolute bottom-full left-0 right-0 mb-3 flex justify-center z-10"
                    >
                        <macro-widget
                            :calories="totalItemCalories"
                            :carbs="totalItemCarbs"
                            :fats="totalItemFats"
                            :protein="totalItemProtein"
                        />
                    </div>
                    <div
                        class="flex flex-row items-center justify-between w-full"
                    >
                        <div
                            v-if="!visualMenu"
                            class="quantity-stepper relative flex flex-row items-center justify-between mr-4 w-36 text-center text-xl font-bold select-none sm:mr-2 sm:w-24"
                        >
                            <div
                                class="cursor-pointer"
                                @click="
                                    quantity > 1 ? quantity-- : (quantity = 1)
                                "
                            >
                                <minus-circle-icon
                                    :class="{ 'opacity-10': quantity === 1 }"
                                    class="w-6 h-6"
                                />
                            </div>
                            <div v-if="hasQuantityInput" class="px-1">
                                <input
                                    v-model="quantity"
                                    class="py-1 text-secondaryactions w-full text-center text-base sm:text-lg border rounded-full focus:outline-gray-700"
                                    type="text"
                                    @change="updateQty"
                                />
                            </div>
                            <div v-else class="text-primary text-2xl">
                                <animated-quantity :value="quantity" />
                            </div>
                            <div
                                class="text-secondaryactions cursor-pointer"
                                @click="increaseQty"
                            >
                                <plus-circle-icon
                                    :class="{
                                        'text-gray-400':
                                            item.trackInventory &&
                                            item.inventory <= quantity
                                    }"
                                    class="w-6 h-6"
                                />
                            </div>
                        </div>
                        <base-button
                            :class="{ 'text-button spinner': isLoading }"
                            class="px-12 py-4 sm:mb-3 md:mb-0 md:ml-3 md:w-auto"
                            data-testid="item-modal-add"
                            size="sm"
                            @clicked="addToCartWithDelay"
                        >
                            <template #slot>
                                <span v-show="!isLoading">
                                    {{ $t('itemModal.addFor') }}
                                    {{ totalItemPrice | customCurrency }}
                                </span>
                                <span class="inline-block"></span>
                            </template>
                        </base-button>
                    </div>
                </div>
            </div>
        </div>
    </modal>
</template>

<script>
import isMobile from '@/helpers/isMobileDevice';
import { maxLength } from 'vuelidate/lib/validators';
import { mapActions, mapState, mapGetters } from 'vuex';
import { getImageUrl, getImageSrcset } from '@/services/image.service';
import PlusCircleIcon from '../icons/PlusCircleIcon';
import MinusCircleIcon from '../icons/MinusCircleIcon';
import DropdownIcon from '../icons/DropdownIcon';
import {
    ItemModalContentPositionEnum,
    FulfillmentMethodsEnum,
    TrackingEventsEnum,
    AllergenDisplayEnum,
    AgeGateEnum
} from '@/enums';
import {
    filterAllergens,
    filterPossibleAllergens,
    getTagsFromModifiers,
    translateTags
} from '@/helpers/allergens';
import { calculateNutritionTotals } from '@/helpers/nutrition';
import * as menuService from '@/services/menu.service';
import toggleAccordion from '@/helpers/accordion';
import ItemModifiers from '@/components/ItemModifiers';
import MacroCalculator from '../MacroCalculator.vue';
import NutritionBreakdownTable from '../NutritionBreakdownTable.vue';
import MacroWidget from '../MacroWidget.vue';
import { allocateFreeModifiersCharges } from '@/helpers/freeModifiersPricing';

const LongBackIcon = () => import('@/components/icons/LongBackIcon');
const ProductVideo = () => ({
    component: import('../elements/ProductVideo')
});
import AnimatedQuantity from '../elements/AnimatedQuantity';

const itemNotesEnum = Object.freeze({
    DISABLED: 'disabled',
    OPTIONAL_HIDDEN: 'optional_hidden',
    OPTIONAL_EXPANDED: 'optional_expanded'
});

export default {
    name: 'item-modal',
    components: {
        ItemModifiers,
        LongBackIcon,
        ProductVideo,
        PlusCircleIcon,
        MinusCircleIcon,
        DropdownIcon,
        MacroCalculator,
        NutritionBreakdownTable,
        MacroWidget,
        AnimatedQuantity
    },
    validations: {
        item: {
            notes: {
                maxLength: maxLength(150)
            }
        }
    },
    provide() {
        return {
            registerValidator: this.registerValidator
        };
    },
    data() {
        return {
            modal: null,
            windowHeight: window.innerHeight,
            item: null,
            quantity: 1,
            isLoading: false,
            tab: 0,
            selectedOption: 1,
            initialY: 0,
            initialScroll: -1,
            marginTop: null,
            soldOut: false,
            contentWrapperHeight: 'auto',
            isOpened: false,
            itemNotesEnum,
            isBrowserBackButton: false,
            tHandler: null,
            validators: [],
            imageLoaded: false
        };
    },
    mounted() {
        window.addEventListener('popstate', this.handleBackButton);
    },
    beforeDestroy() {
        window.removeEventListener('popstate', this.handleBackButton);
        if (this.tHandler) {
            const element = document.getElementById('modal-id');
            if (element) {
                element.removeEventListener('touchmove', this.tHandler);
            }
        }
    },
    computed: {
        allergenTranslations() {
            return this.$t('allergens');
        },
        ...mapState({
            cartItems: state => state.cart.items,
            visualMenu: state => state.venue.venue.visualMenu,
            fulfillmentMethod: state => state.cart.fulfillmentMethod,
            showInventoryQuantity: state =>
                state.venue.venue.showInventoryQuantity,
            allergenDisplay: state => state.venue.venue.allergenDisplay,
            itemContentPosition: state => state.venue.venue.itemContentPosition
        }),
        ...mapGetters({
            cartItemQuantity: 'cart/cartItemQuantity',
            ageOver18Confirmed: 'cart/ageOver18Confirmed',
            allergenDisabled: 'customer/allergenDisabled'
        }),
        stockValue() {
            return this.getStockValue(this.item);
        },
        isContentCentered() {
            return (
                this.itemContentPosition ===
                ItemModalContentPositionEnum.CENTERED
            );
        },
        isMobile() {
            return isMobile();
        },
        isLargeImageStyle() {
            return this.$store.state.venue.venue.modalImageStyle === 'large';
        },
        hasRecommendationsEnabled() {
            return this.$growthbook.isOn('shaped_recs');
        },
        shouldShowMacroWidget() {
            return (
                this.$store.state.venue.venue.macroCalc === 'mobile_widget' &&
                this.isMobile &&
                this.item?.nutrition &&
                (this.item.nutrition.protein !== null ||
                    this.item.nutrition.carbs !== null ||
                    this.item.nutrition.fats !== null ||
                    this.item.nutrition.calories !== null)
            );
        },
        itemHasModifiers() {
            return this.item?.modifierGroups?.length > 0;
        },
        totalItemPrice() {
            let price = this.item.price;

            if (!this.item.modifierGroups) {
                return (price * this.quantity) / 100;
            }

            const { price: totalModifiersPrice } = this.totalsOfModifiers;

            price += totalModifiersPrice;

            return (price * this.quantity) / 100;
        },
        nutritionTotals() {
            return calculateNutritionTotals(
                this.item?.nutrition,
                this.totalsOfModifiers
            );
        },
        totalItemCalories() {
            return this.nutritionTotals.calories;
        },
        totalItemCarbs() {
            return this.nutritionTotals.carbs;
        },
        totalItemFats() {
            return this.nutritionTotals.fats;
        },
        totalItemProtein() {
            return this.nutritionTotals.protein;
        },
        totalItemSalt() {
            return this.nutritionTotals.salt;
        },
        totalItemFibre() {
            return this.nutritionTotals.fibre;
        },
        totalItemCarbsSugars() {
            return this.nutritionTotals.carbsSugars;
        },
        totalItemFatSaturates() {
            return this.nutritionTotals.fatSaturates;
        },
        possibleAllergensList() {
            const translatedTags = this.translateTags(
                this.possibleAllergenTags
            );
            const hasOtherAllergens = this.possibleAllergenTags.some(
                tag => tag !== 1000
            );
            return hasOtherAllergens
                ? translatedTags.filter(
                      (tag, index) => this.possibleAllergenTags[index] !== 1000
                  )
                : translatedTags;
        },
        tagsInSelectedModifiers() {
            return getTagsFromModifiers(this.item.modifierGroups);
        },
        allergenTags() {
            return [
                ...new Set([
                    ...this.filterAllergens(this.item.tags),
                    ...this.filterAllergens(this.tagsInSelectedModifiers)
                ])
            ];
        },
        possibleAllergenTags() {
            return [
                ...new Set([
                    ...this.filterPossibleAllergens(
                        this.tagsInSelectedModifiers
                    ),
                    ...this.filterPossibleAllergens(this.item.tags)
                ])
            ];
        },
        areAllergens() {
            return (
                this.allergenTags.length > 0 ||
                this.possibleAllergenTags.length > 0
            );
        },
        allergensList() {
            const translatedTags = this.translateTags(this.allergenTags);
            const hasOtherAllergens = this.allergenTags.some(
                tag => tag !== 1000
            );
            return hasOtherAllergens
                ? translatedTags.filter(
                      (tag, index) => this.allergenTags[index] !== 1000
                  )
                : translatedTags;
        },
        totalsOfModifiers() {
            let price = 0;
            let calories = 0;
            let carbs = 0;
            let fats = 0;
            let protein = 0;
            let salt = 0;
            let fibre = 0;
            let carbsSugars = 0;
            let fatSaturates = 0;
            let modifiers = [];

            this.item.modifierGroups.map(el => {
                const chargedCountById = allocateFreeModifiersCharges(el);

                if (el.selectedOptions) {
                    for (const option of el.selectedOptions) {
                        const modifier = el.products.find(
                            x => x.id === option.id
                        );

                        const qty = option.quantity || 1;
                        const chargedQty = Math.min(
                            qty,
                            chargedCountById.get(option.id) ?? qty
                        );

                        price += (modifier.price || 0) * chargedQty;

                        calories += (modifier?.nutrition?.calories || 0) * qty;
                        carbs += (modifier?.nutrition?.carbs || 0) * qty;
                        fats += (modifier?.nutrition?.fats || 0) * qty;
                        protein += (modifier?.nutrition?.protein || 0) * qty;
                        salt += (modifier?.nutrition?.salt || 0) * qty;
                        fibre += (modifier?.nutrition?.fibre || 0) * qty;
                        carbsSugars +=
                            (modifier?.nutrition?.carbsSugars || 0) * qty;
                        fatSaturates +=
                            (modifier?.nutrition?.fatSaturates || 0) * qty;

                        modifier.quantity = qty;
                        modifier.chargedQuantity = chargedQty;
                        modifiers.push(modifier);
                    }
                }

                if (el.selectedOption) {
                    const modifier = el.products.find(
                        x => x.id === +el.selectedOption.id
                    );

                    const qty = el.selectedOption.quantity || 1;
                    const chargedQty = Math.min(
                        qty,
                        chargedCountById.get(+el.selectedOption.id) || 0
                    );

                    if (modifier) {
                        calories += modifier?.nutrition?.calories * qty || 0;
                        carbs += modifier?.nutrition?.carbs * qty || 0;
                        fats += modifier?.nutrition?.fats * qty || 0;
                        protein += modifier?.nutrition?.protein * qty || 0;
                        salt += modifier?.nutrition?.salt * qty || 0;
                        fibre += modifier?.nutrition?.fibre * qty || 0;
                        carbsSugars +=
                            modifier?.nutrition?.carbsSugars * qty || 0;
                        fatSaturates +=
                            modifier?.nutrition?.fatSaturates * qty || 0;
                        price += (modifier.price || 0) * chargedQty;

                        modifier.quantity = qty;
                        modifier.chargedQuantity = chargedQty;
                        modifiers.push(modifier);
                    }
                }
            });

            return {
                price,
                calories,
                carbs,
                fats,
                protein,
                salt,
                fibre,
                carbsSugars,
                fatSaturates,
                modifiers
            };
        },
        isBao() {
            return (
                this.$store.state.venue?.venue?.accountId ===
                '5f4e9ddc-3431-43f5-9de0-7aa356564afc'
            );
        },
        hasQuantityInput() {
            return FulfillmentMethodsEnum.isCateringMethod(
                this.fulfillmentMethod
            );
        },
        enableItemNotes() {
            const setting = this.$store.state.venue.venue.enableItemNotes;
            // Handle boolean values for backward compatibility
            if (typeof setting === 'boolean') {
                return setting
                    ? this.itemNotesEnum.OPTIONAL_HIDDEN
                    : this.itemNotesEnum.DISABLED;
            }
            return setting;
        },
        shouldShowItemNotes() {
            return this.enableItemNotes !== this.itemNotesEnum.DISABLED;
        },
        shouldExpandItemNotes() {
            return (
                this.enableItemNotes === this.itemNotesEnum.OPTIONAL_EXPANDED
            );
        },
        modalLayoutStyle() {
            return this.$store.state.venue.venue.modalLayoutStyle;
        },
        modalWidth() {
            if (this.isMobile) return 550;

            let baseWidth;
            switch (this.modalLayoutStyle) {
                case 'desktop_wide':
                    baseWidth = 1200;
                    break;
                case 'desktop_large':
                    baseWidth = 1400;
                    break;
                case 'desktop_extra_large':
                    baseWidth = 1600;
                    break;
                default:
                    return 550;
            }

            return Math.min(baseWidth, window.innerWidth - 30);
        },
        modalLayoutClass() {
            if (this.isMobile) return '';

            switch (this.modalLayoutStyle) {
                case 'desktop_wide':
                    return 'item-modal-two-column';
                case 'desktop_large':
                    return 'item-modal-two-column item-modal-size-large';
                case 'desktop_extra_large':
                    return 'item-modal-two-column item-modal-size-extra-large';
                default:
                    return '';
            }
        },
        isTwoColumnLayout() {
            return (
                !this.isMobile &&
                [
                    'desktop_wide',
                    'desktop_large',
                    'desktop_extra_large'
                ].includes(this.modalLayoutStyle)
            );
        },
        isAllergenDisplayExtended() {
            return this.allergenDisplay === AllergenDisplayEnum.EXTENDED;
        }
    },
    methods: {
        toggleAccordion,
        registerValidator(fn) {
            this.validators.push(fn);
        },
        handleBackButton() {
            this.isBrowserBackButton = true;
        },
        ...mapActions({
            addProductToCart: 'cart/addProductToCart',
            cartTotalWithDiscount: 'cart/cartTotalWithDiscount'
        }),
        translateTags(tags) {
            return translateTags(tags, this.allergenTranslations);
        },
        filterAllergens(tags) {
            return filterAllergens(tags);
        },
        filterPossibleAllergens(tags) {
            return filterPossibleAllergens(tags);
        },
        getImageUrl(imagePath, isTwoColumnLayout) {
            return getImageUrl(imagePath, isTwoColumnLayout);
        },
        getImageSrcset(imagePath, isTwoColumnLayout) {
            return getImageSrcset(imagePath, isTwoColumnLayout);
        },
        getStockValue(item) {
            if (!item.trackInventory || this.showInventoryQuantity === 'hide') {
                return null;
            }

            if (this.showInventoryQuantity === 'exact') {
                return this.$t('itemModal.stockQuantity', {
                    value: item.inventory || 0
                });
            } else if (
                this.showInventoryQuantity === 'low_only' &&
                item.inventory <= 10
            ) {
                return this.$t('itemModal.stockLow');
            }
        },
        beforeEnterTransition() {
            if (!this.$refs.contentWrapper) {
                return;
            }

            this.$refs.contentWrapper.style.height = this.contentWrapperHeight;
        },
        enterTransition(el) {
            if (!this.$refs.contentWrapper) {
                return;
            }

            this.$refs.contentWrapper.style.height = el.scrollHeight + 'px';
        },
        afterEnterTransition() {
            if (!this.$refs.contentWrapper) {
                return;
            }

            this.contentWrapperHeight =
                this.$refs.contentWrapper.style.height || 0;
            this.$refs.contentWrapper.style.height = 'auto';
        },
        async addToCartWithDelay() {
            this.isLoading = true;

            const isValid = await this.validate();

            if (!isValid) {
                this.tab = 0;
                this.isLoading = false;

                return;
            }

            if (this.item.trackInventory) {
                try {
                    const isAvailable =
                        await menuService.checkMenuItemAvailability(
                            this.item,
                            this.quantity
                        );

                    if (!isAvailable) {
                        this.soldOut = true;
                        this.isLoading = false;

                        return;
                    }
                } catch (e) {
                    this.isLoading = false;
                }
            }
            setTimeout(
                () => {
                    const { modifiers } = this.totalsOfModifiers;

                    this.addProductToCart([
                        this.item,
                        this.quantity,
                        modifiers,
                        'item_modal'
                    ]);

                    if ('vibrate' in navigator) {
                        window.navigator.vibrate(200);
                    }

                    this.$v.$reset();

                    this.cartTotalWithDiscount();

                    this.isLoading = false;
                    this.hideItemModal();
                },
                this.item.trackInventory ? 0 : 150
            );
        },
        async validate() {
            this.$v.$touch();

            const validators = this.validators.map(fn => fn());

            return !this.$v.$invalid && validators.every(Boolean);
        },
        increaseQty() {
            if (!this.item.trackInventory) {
                this.quantity++;
            } else {
                const cartItemQuantity = this.cartItemQuantity(this.item.id);
                let inventory = this.item.inventory;

                if (cartItemQuantity) {
                    inventory -= cartItemQuantity;
                }

                if (inventory > this.quantity) {
                    this.quantity++;
                }
            }
        },
        beforeOpen(event) {
            this.validators = [];
            this.marginTop = null;
            this.item = event.params.item;
            this.item.notes = '';
            this.tab = 0;
            this.quantity = 1;
            this.soldOut = false;
            this.imageLoaded = false;
            this.$v.$reset();

            this.triggerAgeGate();

            if (this.item && this.item.modifierGroups) {
                this.item.modifierGroups.map(el => {
                    el.selectedOption ? (el.selectedOption = null) : null;
                    el.selectedOptions ? (el.selectedOptions = []) : null;
                });
            }

            // https://github.com/euvl/vue-js-modal/issues/529#issuecomment-600733392
            // First we get the viewport height and we multiple it by 1% to get a value for a vh unit
            let vh = window.innerHeight * 0.01;
            // Then we set the value in the --vh custom property to the root of the document
            document.documentElement.style.setProperty('--vh', `${vh}px`);
        },
        opened() {
            this.isBrowserBackButton = false;

            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';

            this.contentWrapperHeight =
                this.$refs.contentWrapper.scrollHeight + 'px';
            const element = document.getElementById('modal-id');

            if (element) {
                if (!this.isMobile) {
                    const positionInfo = element.getBoundingClientRect();
                    const elementHeight = positionInfo.height;
                    const clientHeight = document.documentElement.clientHeight;

                    if (clientHeight < elementHeight) {
                        this.marginTop = '20px';
                    }
                } else {
                    const handler = event => this.moveHandler(event);
                    this.tHandler = this.throttled(16, handler);
                    element.addEventListener('touchmove', this.tHandler);
                }
            }
            setTimeout(() => {
                this.isOpened = true;
            }, 200);

            this.waitForImageToLoad();

            this.$router.push({ hash: '#product' }).catch(() => {});

            if (this.$analytics) {
                this.$analytics.track(TrackingEventsEnum.VIEWED_PRODUCT, {
                    id: this.item.id,
                    name: this.item.name,
                    currency: this.$store.state.venue?.venue?.currencyCode,
                    price: this.item.price,
                    image: this.item.image,
                    plu: this.item.plu,
                    sku: this.item.sku
                });
            }
        },
        closed() {
            document.body.style.width = '';
            document.body.style.overflow = '';

            this.isOpened = false;
            this.$router.replace({ hash: null }).catch(() => {});

            // Only call history.back() if we're not at risk of navigating to external site
            if (!this.isBrowserBackButton) {
                // Check if we have multiple entries and current URL has the hash
                if (
                    window.history.length > 1 &&
                    window.location.hash === '#product'
                ) {
                    history.back();
                }
            }
        },
        hideItemModal() {
            document.body.style.width = '';
            document.body.style.overflow = '';

            this.$modal.hide('item-modal');
        },
        startHandler(event) {
            if (event.type !== 'touchstart') {
                return;
            }

            // If the user is using two fingers (a pinch gesture), do nothing
            if (event.touches.length > 1) return;

            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';

            this.initialY = event.touches[0].clientY;
            this.initialScroll =
                document.getElementsByClassName(
                    'v--modal-overlay'
                )[0].scrollTop;
        },
        throttled(delay, fn) {
            let lastCall = 0;

            return function (...args) {
                const now = new Date().getTime();

                if (now - lastCall < delay) {
                    return;
                }

                lastCall = now;

                return fn(...args);
            };
        },
        moveHandler(event) {
            // If the user is pinching, ignore
            if (event.touches.length > 1) return;

            const modal = document.getElementById('modal-id');

            if (!modal || modal.scrollTop > 10) {
                return;
            }

            if (
                this.initialScroll === 0 &&
                event.touches[0].clientY > this.initialY
            ) {
                document.getElementsByClassName(
                    'v--modal-overlay'
                )[0].scrollTop = 0;

                modal.style.transition = 'none';
                modal.style.transform =
                    'translateY(' +
                    (event.touches[0].clientY - this.initialY) +
                    'px)';
            }
        },
        swipeHandler() {
            const modal = document.getElementById('modal-id');

            if (modal.scrollTop > 10) {
                return;
            }

            if (this.initialScroll === 0) {
                setTimeout(() => {
                    document.body.style.width = '';
                    document.body.style.overflow = '';
                }, 100);

                this.$modal.hide('item-modal');
            }
        },
        endHandler(event) {
            // If the user was pinching, ignore
            if (event.touches && event.touches.length > 1) return;

            if (event.type !== 'touchend') {
                return;
            }

            const modal = document.getElementById('modal-id');
            modal.style.transition = 'transform .26s';
            modal.style.transform = 'translateY(0)';
        },
        waitForImageToLoad() {
            const img = document.getElementById('itemModalImage');

            if (!img) {
                return;
            }

            img.onload = () => {
                let el = document.getElementById('imageContainer');

                if (el) {
                    el.classList.add('skeleton-loading-background-fade-out');

                    // Clean up after fade-out transition completes
                    setTimeout(() => {
                        el.classList.remove('skeleton-loading-background');
                        el.classList.remove(
                            'skeleton-loading-background-fade-out'
                        );
                    }, 400); // Match the 0.4s transition duration
                }

                // Trigger fade-in animation
                this.imageLoaded = true;
            };
        },
        updateQty(event) {
            const qty = parseInt(event.target.value, 10);

            if (isNaN(qty) || qty < 1) {
                this.quantity = 1;
            } else {
                let inventory = this.item.trackInventory
                    ? this.item.inventory
                    : 999;
                const cartItemQuantity = this.cartItemQuantity(this.item.id);

                if (cartItemQuantity && this.item.trackInventory) {
                    inventory -= cartItemQuantity;
                }

                this.quantity = Math.min(qty, inventory);
            }
        },
        triggerAgeGate() {
            if (
                this.$store.state.venue?.venue?.ageGated ===
                    AgeGateEnum.DISABLED ||
                this.ageOver18Confirmed
            ) {
                return;
            }
            const hasAlcoholTag = this.item.tags?.includes(1);

            if (hasAlcoholTag) {
                this.$modal.show('age-verification-modal');
            }
        }
    }
};
</script>
<style lang="css">
.collapsed {
    height: 0;
    visibility: collapse;
}
</style>
<style src="@/assets/css/item-modal.css"></style>
